import {
    Gamepad2,
    TrendingUp,
    Wind,
    DoorOpen,
    AlertTriangle,
    Castle,
    RotateCcw,
    Leaf,
    Trophy,
    Heart,
    Backpack,
    VenetianMask,
    HeartHandshake,
    Flame,
    Crown,
    Moon,
    Rocket,
    Fingerprint,
    Swords,
    Landmark,
} from "lucide-react";
import type { ComponentType } from "react";
import type { StoryGenre } from "@/lib/actions/story-actions";

// ─── Genre Registry ──────────────────────────────────────
// THE single source of truth for genre *identity* (enum value, URL slug,
// display label, icon). Everything else derives from this:
//   - genre-status.ts   → GENRES, ALL_GENRE_LABELS, VALID_GENRES
//   - content/genres.ts → joins each entry with its curated SEO copy
//   - filter-utils.ts   → enum → slug URL mapping
//
// Adding a genre is exactly two coordinated edits:
//   1. Add the value to `story_genre` in db/schema.ts (+ a Drizzle migration).
//   2. Add an entry here AND a matching GENRE_COPY block in content/genres.ts.
// The copy join in content/genres.ts THROWS at module load if step 2 is half
// done, so a missing landing page fails the build instead of silently routing
// to /browse.
//
// This module is intentionally light (icons + identity only) so it is safe to
// import from client components like the editor's genre picker. Do NOT import
// content/genres.ts here — that would pull the heavy SEO copy into the client
// bundle.

export interface GenreIdentity {
    enumValue: StoryGenre;
    slug: string;
    label: string;
    icon: ComponentType<{ className?: string }>;
}

export const GENRE_REGISTRY: GenreIdentity[] = [
    { enumValue: "litrpg", slug: "litrpg", label: "LitRPG", icon: Gamepad2 },
    { enumValue: "progression_fantasy", slug: "progression-fantasy", label: "Progression Fantasy", icon: TrendingUp },
    { enumValue: "cultivation", slug: "cultivation", label: "Cultivation", icon: Wind },
    { enumValue: "isekai", slug: "isekai", label: "Isekai", icon: DoorOpen },
    { enumValue: "system_apocalypse", slug: "system-apocalypse", label: "System Apocalypse", icon: AlertTriangle },
    { enumValue: "dungeon_core", slug: "dungeon-core", label: "Dungeon Core", icon: Castle },
    { enumValue: "regression", slug: "regression", label: "Regression", icon: RotateCcw },
    { enumValue: "cozy_fantasy", slug: "cozy-fantasy", label: "Cozy Fantasy", icon: Leaf },
    { enumValue: "sports", slug: "sports", label: "Sports", icon: Trophy },
    { enumValue: "romance", slug: "romance", label: "Romance", icon: Heart },
    { enumValue: "romantasy", slug: "romantasy", label: "Romantasy", icon: Flame },
    { enumValue: "young_adult", slug: "young-adult", label: "Young Adult", icon: Backpack },
    { enumValue: "thriller", slug: "thriller", label: "Thriller", icon: VenetianMask },
    { enumValue: "contemporary_romance", slug: "contemporary-romance", label: "Contemporary Romance", icon: HeartHandshake },
    { enumValue: "historical", slug: "historical", label: "Historical", icon: Crown },
    { enumValue: "paranormal", slug: "paranormal", label: "Paranormal", icon: Moon },
    { enumValue: "sci_fi", slug: "sci-fi", label: "Sci-Fi", icon: Rocket },
    { enumValue: "mystery", slug: "mystery", label: "Mystery", icon: Fingerprint },
    { enumValue: "action", slug: "action", label: "Action", icon: Swords },
    { enumValue: "kingdom_building", slug: "kingdom-building", label: "Kingdom Building", icon: Landmark },
];

// Legacy enum values (`story_genre`) kept for old data but intentionally not
// surfaced in the filter UI or given landing pages. They still need display
// labels for badges rendered on already-tagged stories.
export const LEGACY_GENRE_LABELS: Record<string, string> = {
    fantasy: "Fantasy",
};
